import { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { Icon } from '../../ui/Icon';
import { RoomForm } from './RoomForm';

export function RoomsPage() {
  const qc = useQueryClient();
  const gridRef = useRef<AgGridReact | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data: rooms, isLoading } = useQuery({
    queryKey: ['rooms', showInactive],
    queryFn: () =>
      api
        .get('/resources', {
          params: { type: 'ROOM', ...(showInactive ? {} : { activeOnly: 'true' }) },
        })
        .then((r) => r.data),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) =>
      api.patch(`/resources/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rooms'] });
      toast.success('Room updated');
    },
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/resources/${id}`, { active }).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['rooms'] });
      toast.success(vars.active ? 'Room reactivated' : 'Room deactivated');
    },
  });

  const cols = useMemo<ColDef[]>(
    () => [
      { headerName: 'Name', field: 'displayName', pinned: 'left', editable: true, width: 220 },
      { headerName: 'Capacity', field: 'capacity', editable: true, width: 110 },
      { headerName: 'Place', field: 'placeOfService', width: 160 },
      { headerName: 'Notes', field: 'notes', editable: true, flex: 1, minWidth: 200 },
      {
        headerName: 'Status',
        field: 'active',
        width: 110,
        cellRenderer: (p: any) => (
          <span className={p.value ? 'pill-success' : 'pill-muted'}>
            {p.value ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        headerName: 'Actions',
        pinned: 'right',
        width: 220,
        sortable: false,
        cellRenderer: (p: any) => (
          <div className="flex h-full items-center gap-3 text-xs">
            <button
              className="text-brand-700 hover:underline"
              onClick={() => {
                setEditing(p.data);
                setFormOpen(true);
              }}
            >
              Edit
            </button>
            {p.data.active ? (
              <button
                className="text-rose-600 hover:underline"
                onClick={() => {
                  if (confirm(`Deactivate ${p.data.displayName}?`)) {
                    setActive.mutate({ id: p.data.id, active: false });
                  }
                }}
              >
                Deactivate
              </button>
            ) : (
              <button
                className="text-emerald-700 hover:underline"
                onClick={() => setActive.mutate({ id: p.data.id, active: true })}
              >
                Reactivate
              </button>
            )}
          </div>
        ),
      },
    ],
    [setActive],
  );

  function onCellValueChanged(e: any) {
    if (!e.colDef.editable) return;
    update.mutate({ id: e.data.id, patch: { [e.colDef.field!]: e.newValue } });
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Rooms</h1>
          <p className="text-sm text-ink-subtle">
            Bookable rooms / stations. Capacity controls concurrent bookings.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <button
            className="btn-secondary h-9 whitespace-nowrap"
            onClick={() => gridRef.current?.api.exportDataAsCsv({ fileName: 'rooms.csv' })}
          >
            Export CSV
          </button>
          <button
            className="btn-primary h-9 whitespace-nowrap"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Icon name="building" size={14} /> New room
          </button>
        </div>
      </header>

      <div className="ag-theme-quartz flex-1 overflow-hidden rounded-card shadow-card ring-1 ring-line">
        {rooms?.length === 0 && !isLoading ? (
          <div className="grid h-full place-items-center p-8 text-center text-sm text-ink-subtle">
            <div>
              <p className="font-medium text-ink">No rooms yet.</p>
              <p className="mt-1">
                Click <span className="font-medium text-ink">New room</span> to add your first one.
                Rooms become bookable resource columns on the schedule.
              </p>
            </div>
          </div>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={rooms ?? []}
            columnDefs={cols}
            animateRows
            singleClickEdit
            stopEditingWhenCellsLoseFocus
            onCellValueChanged={onCellValueChanged}
            overlayLoadingTemplate={isLoading ? 'Loading…' : ''}
          />
        )}
      </div>

      <RoomForm open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
    </div>
  );
}
